import { nameSimilarity } from './nameSimilarity.js';
import { valueSimilarity } from './valueSimilarity.js';
import { contentMatch } from './contentMatch.js';

export const CONFIDENCE = Object.freeze({
  HIGH: 0.8,
  MEDIUM: 0.5,
});

function getFieldDomain(field) {
  return field?.validation?.domain || field?.domain || field?.domainValue || null;
}

function getFieldId(field) {
  return field.id || field.name || field.label;
}

function getFieldLabels(field) {
  const labels = [field.id, field.name, field.label, field.alias].filter(Boolean);
  return labels.length ? labels : [getFieldId(field)];
}

export function scorePair(column, field) {
  const labels = getFieldLabels(field);
  let nameScore = 0;
  for (const label of labels) {
    const s = nameSimilarity(column.name, label);
    if (s > nameScore) nameScore = s;
  }

  const domain = getFieldDomain(field);
  let valueScore = 0;
  if (domain && Array.isArray(domain) && domain.length > 0) {
    valueScore = valueSimilarity(column.sample || [], domain);
  }

  const content = contentMatch(column, field);
  const contentScore = content.score;
  const strongRegex = content.kind === 'regex' && contentScore >= 0.9;

  let score;
  if (domain && domain.length > 0) {
    score = Math.max(nameScore, valueScore, 0.5 * nameScore + 0.5 * valueScore);
  } else if (strongRegex) {
    score = Math.min(1, 0.85 + 0.1 * contentScore + 0.05 * nameScore);
  } else if (contentScore > 0) {
    score = Math.max(nameScore, 0.4 * nameScore + 0.6 * contentScore);
  } else {
    score = nameScore;
  }

  return { nameScore, valueScore, contentScore, score };
}

export function classifyConfidence(score) {
  if (score >= CONFIDENCE.HIGH) return 'high';
  if (score >= CONFIDENCE.MEDIUM) return 'medium';
  return 'low';
}

export function proposeMapping(columns, fields, options = {}) {
  const minScore = options.minScore ?? 0.3;
  const usedColumns = new Set();
  const proposals = [];

  const scored = [];
  for (const field of fields) {
    for (const column of columns) {
      const { nameScore, valueScore, contentScore, score } = scorePair(column, field);
      if (score >= minScore) {
        scored.push({ field, column, nameScore, valueScore, contentScore, score });
      }
    }
  }
  scored.sort((a, b) => b.score - a.score);

  const assigned = new Map();
  for (const candidate of scored) {
    const fid = getFieldId(candidate.field);
    if (assigned.has(fid)) continue;
    if (usedColumns.has(candidate.column.name)) continue;
    assigned.set(fid, candidate);
    usedColumns.add(candidate.column.name);
  }

  for (const field of fields) {
    const fid = getFieldId(field);
    const c = assigned.get(fid);
    if (c) {
      proposals.push({
        fieldId: fid,
        column: c.column.name,
        score: c.score,
        nameScore: c.nameScore,
        valueScore: c.valueScore,
        contentScore: c.contentScore,
        confidence: classifyConfidence(c.score),
      });
    } else {
      proposals.push({
        fieldId: fid,
        column: null,
        score: 0,
        nameScore: 0,
        valueScore: 0,
        contentScore: 0,
        confidence: 'none',
      });
    }
  }

  return proposals;
}
